import React from 'react';

import { Divider, Flex, Skeleton, Stack } from '@chakra-ui/react';
import { REVIEW_FORM_KEYS } from 'reducers/reviewFormSlice';
import { REVIEW_RESULT_VIEW, useCurrentProvider, useSelectedReviewResultView } from 'reducers/reviewResultSlice';
import { QuestionResponseStatusTag } from 'screens/performanceReview/components/newFormComponents/QuestionResponseStatusTag';
import { OverallResultScore } from 'screens/performanceReview/components/newResultComponents/OverallResultScore';
import useReviewResultParams from 'screens/performanceReview/hooks/useReviewResultParams';

import { Competency } from 'assets/assetsV1/iconComponents';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from 'uiToolkitV1/Accordion';
import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useReviewResultProviderEmpInfo } from '../components/sidebar/hooks/dataHooks';
import CompetencyScrollableNav from './components/CompetencyScrollableNav';
import PeopleViewCompetencyScrollableNav from './components/PeopleViewCompetencyScrollableNav';
import { useCompetencyResultData } from './hooks/dataHooks';

const CompetencyResult = () => {
  const { employeeId, providerId, reviewId } = useReviewResultParams();
  const selectedView = useSelectedReviewResultView();
  const currentProvider = useCurrentProvider();

  const { data, isLoading } = useCompetencyResultData({
    employeeId,
    providerId,
    responseId: currentProvider.reviewId, // <- will be used to show the submitted
    reviewId,
    providerType: currentProvider.providerType,
  });

  const { competencyQuestions, employee: firstResponseEmp } = data ?? {};

  const providerInfo = useReviewResultProviderEmpInfo({ empId: firstResponseEmp?.id });

  const firstResponseEmp1 = {
    ...firstResponseEmp,
    providerType: providerInfo?.providerType,
    peerType: providerInfo?.peerType,
  };

  const defaultOpenIndexes = competencyQuestions?.map((_, index) => index);

  if (isLoading) {
    return (
      <Paper
        bg="#F5F7F9"
        p="16px"
        sx={{
          boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.25)',
        }}
        mb="12px"
      >
        <Stack>
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
        </Stack>
      </Paper>
    );
  }
  if (competencyQuestions?.length === 0) return null;
  return (
    <Paper
      bg="#F5F7F9"
      p="16px"
      sx={{
        boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.25)',
      }}
      mb="12px"
    >
      <Flex alignItems="center">
        <Flex p="0 8px" flexDir="column">
          <TextBodyMedium bold>Competency Review</TextBodyMedium>
          <TextLabel color="text.g2">
            {competencyQuestions?.length} {competencyQuestions?.length === 1 ? 'Competency' : 'Competencies'}
          </TextLabel>
        </Flex>
        <OverallResultScore reviewForm={REVIEW_FORM_KEYS.COMPETENCY} />
      </Flex>
      <Divider color="#CFD7DF" opacity={1} my="10px" />

      <Paper p="0" overflow="hidden" w="100%">
        <Accordion allowMultiple allowToggle defaultIndex={defaultOpenIndexes}>
          {competencyQuestions?.map(({ tag, questions }) => (
            <AccordionItem>
              {/* just another UX inconsistency, doing it for now! */}
              <AccordionButton sx={{ background: '#FFFFFF !important' }}>
                <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
                  <Flex alignItems="center" flexGrow={1} mr={4}>
                    <Competency color="functional.AF5C20" boxSize="24px" fill="transparent" mr={2} />
                    <TextBodyMedium bold>{tag?.displayName}</TextBodyMedium>
                  </Flex>
                  {questions[0].answerType !== 'NONE' && selectedView === REVIEW_RESULT_VIEW.PEOPLE && (
                    <Flex>
                      <QuestionResponseStatusTag questions={questions} isValidateForm={false} showIcon={false} />
                    </Flex>
                  )}
                </Flex>
              </AccordionButton>
              <AccordionPanel borderTop="0 !important">
                {questions.map((question) => (
                  <Flex flexDir="column">
                    <TextBodyMedium bold textAlign="left" p={3}>
                      {question?.description}
                    </TextBodyMedium>
                    {/* hardcoding question test for now, need updated UX for it */}
                    {selectedView === REVIEW_RESULT_VIEW.PEOPLE ? (
                      <PeopleViewCompetencyScrollableNav
                        {...question}
                        questionText={question?.description}
                        questionId={question?.id}
                        competencyId={tag?.id}
                      />
                    ) : (
                      <CompetencyScrollableNav
                        {...question}
                        questionText={question?.description}
                        questionId={question?.id}
                        competencyId={tag?.id}
                        question={question}
                        firstResponseEmp={firstResponseEmp1}
                        isUseStartScrollBlock={questions.length > 1}
                      />
                    )}
                  </Flex>
                ))}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Paper>
    </Paper>
  );
};

export default CompetencyResult;
